<script setup lang="ts">
import AudioFileContent from '@/modules/Project/AudioFileContent.vue'
import CsvFileContent from '@/modules/Project/CsvFileContent.vue'
import type { FileType } from '@/modules/Project/Fields/utils/file'
import ImageFileContent from '@/modules/Project/ImageFileContent.vue'
import PdfViewer from '@/modules/Project/PdfViewer.vue'
import HTMLFileContent from '@/sharedComponents/HTMLFileContent.vue'
import TextFileContent from '@/sharedComponents/TextFileContent.vue'
import type { HighlightEventPayload, TextLayerLoadedEventPayload } from '@tato30/vue-pdf'

/**
 * In a number of places in the frontend, we render a file inline. This component
 * is a wrapper around the different file types that can be rendered inline.
 * Eventually this should probably be a registry, but for now the child component
 * signatures are different enough that it's not worth it. We should look to
 * refactor this in the future if more inline file types are added.
 */

defineProps<{
  fileType: Exclude<FileType, undefined>
  url: string
  /**
   * Only used when rendering a PDF. If set and the PDF has a text layer, all
   * instances of this string will be highlighted.
   */
  highlightText?: string
  border?: boolean
  fullWidthZoomControls?: boolean
}>()

defineEmits<{
  'pdf:highlight': [payload: HighlightEventPayload]
  'pdf:textLoaded': [payloaded: TextLayerLoadedEventPayload]
}>()
</script>

<template>
  <PdfViewer
    v-if="fileType === 'pdf'"
    :file-path="url"
    :highlight-text="highlightText"
    :border="border"
    :full-width-zoom-controls="fullWidthZoomControls"
    @highlight="$emit('pdf:highlight', $event)"
    @text-loaded="$emit('pdf:textLoaded', $event)"
  />
  <AudioFileContent
    v-else-if="fileType === 'audio'"
    :url="url"
    :border="border"
  />
  <ImageFileContent
    v-else-if="fileType === 'image'"
    :url="url"
    :border="border"
    class="rounded-md"
    :full-width-zoom-controls="fullWidthZoomControls"
  />
  <CsvFileContent
    v-else-if="fileType === 'csv'"
    :url="url"
    :border="border"
  />
  <TextFileContent
    v-else-if="fileType === 'txt' || fileType === 'json'"
    :file-type="fileType"
    :url="url"
    :border="border"
  />
  <HTMLFileContent
    v-else-if="fileType === 'html'"
    :url="url"
  />
</template>
